/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge, AvatarMedium,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  MultiLanguageSelect
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <MultiLanguageSelect />
        {/*<AvatarLarge className={css.avatar} user={currentUser} disableProfileLink />*/}
        <div className={classNames(css.content, css.mobileContent)}>
          <div className={css.accountLinksWrapper}>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SignupPage'))}
              name="SignupPage"
            >
              <FormattedMessage id="TopbarMobileMenu.signUpLink" />
            </NamedLink>

            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('LoginPage'))}
              name="LoginPage"
            >
              <FormattedMessage id="TopbarMobileMenu.loginMobileLink" />
            </NamedLink>

            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              <FormattedMessage id="TopbarMobileMenu.exploreLink" />
            </NamedLink>
          </div>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={''} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  const inboxTab = currentUserHasListings ? 'my-shop' : 'my-brand';

  return (
    <div className={css.root}>
      <MultiLanguageSelect />
      <AvatarMedium className={css.avatar} user={currentUser} disableProfileLink />
      <div className={css.content}>
        {/*<span className={css.greeting}>*/}
        {/*  <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />*/}
        {/*</span>*/}
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
            name="SearchPage"
          >
            <FormattedMessage id="TopbarMobileMenu.exploreLink" />
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ManageListingsPage:my-brand')
            )}
            name="ManageBrandsAndProductsListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.myBrandLink" />
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ManageListingsPage:my-shop')
            )}
            name="ManageShopsAndDisplaysListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.myShopLink" />
          </NamedLink>

          {/*<NamedLink*/}
          {/*  className={classNames(css.navigationLink, currentPageClass('LandingPage'))}*/}
          {/*  name="LandingPage"*/}
          {/*>*/}
          {/*  <FormattedMessage id="TopbarMobileMenu.salesHubLink" />*/}
          {/*</NamedLink>*/}

          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {/* <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </NamedLink> */}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {/*<NamedLink*/}
          {/*  className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}*/}
          {/*  name="ProfileSettingsPage"*/}
          {/*>*/}
          {/*  <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />*/}
          {/*</NamedLink>*/}
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      {/*<div className={css.footer}>*/}
      {/*  <NamedLink className={css.createNewListingLink} name="NewListingPage">*/}
      {/*    <FormattedMessage id="TopbarMobileMenu.newListingLink" />*/}
      {/*  </NamedLink>*/}
      {/*</div>*/}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
